import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { LayoutDashboard, ShieldCheck, CreditCard, Users, CheckCircle2, XCircle, Power, Plus, Pencil, Trash2, KeyRound, Receipt, Bell, FlaskConical, Loader2, Globe, Eye, Lock, Palette, FileText, Search, ChevronDown, ChevronRight, Info, AlertTriangle, RefreshCw, ArrowLeftRight, MessageSquare, Headphones, Send, ChevronUp, Clock, Hash, User, ScanSearch, Zap, Radio, ArrowRight, Download, Calendar, X, Filter, Activity, Landmark, Settings, UserPlus } from "lucide-react";
import SaaSPlans from "./SaaSPlans";
import { toast } from "sonner";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DashLayout, { StatCard, Badge } from "@/components/DashLayout";
import { api } from "@/lib/api";
import Pagination, { usePagination } from "@/components/Pagination";
import NotificationProviders from "./NotificationProviders";
import TransactionAnalyzerModal from "@/components/TransactionAnalyzerModal";

import TemplateClassic from "@/components/templates/TemplateClassic";
import TemplateModernDark from "@/components/templates/TemplateModernDark";
import TemplateVibrant from "@/components/templates/TemplateVibrant";
import TemplateMinimal from "@/components/templates/TemplateMinimal";
import TemplateEnterprise from "@/components/templates/TemplateEnterprise";

const TEMPLATES_COMPONENTS = {
  classic: TemplateClassic,
  modern_dark: TemplateModernDark,
  vibrant: TemplateVibrant,
  minimal: TemplateMinimal,
  enterprise: TemplateEnterprise,
};

const TEMPLATE_COLOR_LABELS = {
  classic: { p: "Primary / Buttons", a: "Price Accents", n: "Header & Footer" },
  modern_dark: { p: "Gradient Start (Primary)", a: "Gradient End (Accent)", n: null },
  vibrant: { p: "Gradient Start", a: "Gradient End", n: "Footer Background" },
  minimal: { p: "Text & Accents (Dark)", a: null, n: null },
  enterprise: { p: "Highlights & Accents", a: null, n: "Theme Base (Nav/Hero)" },
};

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  if (h.length === 3) return `${parseInt(h[0] + h[0], 16)} ${parseInt(h[1] + h[1], 16)} ${parseInt(h[2] + h[2], 16)}`;
  if (h.length === 6) return `${parseInt(h.slice(0, 2), 16)} ${parseInt(h.slice(2, 4), 16)} ${parseInt(h.slice(4, 6), 16)}`;
  return null;
}

function darkenHex(hex, amount = 20) {
  let r = parseInt(hex.slice(1, 3), 16) - amount;
  let g = parseInt(hex.slice(3, 5), 16) - amount;
  let b = parseInt(hex.slice(5, 7), 16) - amount;
  r = Math.max(0, r).toString(16).padStart(2, '0');
  g = Math.max(0, g).toString(16).padStart(2, '0');
  b = Math.max(0, b).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

const nav = [
  { to: ".", label: "Dashboard", icon: LayoutDashboard, end: true },

  // Tenant Management
  { to: "admins", label: "Admins", icon: Users, group: "Tenants" },
  { to: "gateway-configs", label: "Admin GW Configs", icon: KeyRound, group: "Tenants" },
  { to: "leads", label: "Demo Requests", icon: UserPlus, group: "Tenants" },
  { to: "saas-plans", label: "SaaS Plans", icon: KeyRound, group: "Tenants" },

  // Monitoring
  { to: "transactions", label: "Transactions", icon: Receipt, group: "Monitoring" },
  { to: "route-transfers", label: "Settlement Report", icon: Landmark, group: "Monitoring" },
  { to: "recharge-logs", label: "Recharge Logs", icon: FileText, group: "Monitoring" },
  { to: "webhooks", label: "Webhook Events", icon: Bell, group: "Monitoring" },

  // Platform
  { to: "gateways", label: "Payment Gateways", icon: CreditCard, group: "Platform" },
  { to: "notifications", label: "Notification Providers", icon: MessageSquare, group: "Platform" },
  { to: "complaints", label: "Admin Complaints", icon: Headphones, group: "Platform" },
  { to: "settings", label: "Platform Settings", icon: Settings, group: "Platform" },
];

function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/superadmin/dashboard").then(r => setData(r.data)).catch(() => { }); }, []);
  if (!data) return <div className="text-slate-500">Loading...</div>;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard testid="sa-stat-total" label="Total admins" value={data.admins_total} accent="nav" />
        <StatCard testid="sa-stat-pending" label="Pending review" value={data.admins_pending} accent="amber" hint="Awaiting approval" />
        <StatCard testid="sa-stat-active" label="Active tenants" value={data.admins_active} accent="accent" />
        <StatCard testid="sa-stat-volume" label="Recharge today ₹" value={`₹${(data.recharge_volume_today || 0).toLocaleString()}`} accent="primary" />
      </div>
      <div className="grid md:grid-cols-1 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-lg text-brand-nav">Payment gateways</h3>
            <Badge tone="info">{data.gateways_active} enabled</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-2">Manage all gateways and configure credentials for each admin centrally.</p>
          <Link to="/superadmin/gateways" data-testid="sa-cta-gateways" className="mt-4 inline-flex text-sm font-semibold text-brand-primary hover:underline">Manage gateways →</Link>
        </div>
      </div>
    </div>
  );
}

function Admins() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [modal, setModal] = useState(null); // null | 'add' | 'edit' | 'password' | 'domain'
  const [target, setTarget] = useState(null); // admin being acted on
  const [addForm, setAddForm] = useState({ name: "", email: "", mobile: "", password: "", business_name: "", plan: "annual", template_id: "classic", color_primary: "#185FA5", color_accent: "#1D9E75", color_nav: "#0C447C", panel_color_primary: "#185FA5", panel_color_accent: "#1D9E75", panel_color_nav: "#0C447C", headline: "", tagline: "" });
  const [editForm, setEditForm] = useState({ business_name: "", contact_person: "", mobile: "", email: "" });
  const [pwForm, setPwForm] = useState({ new_password: "" });
  const [rejectForm, setRejectForm] = useState({ note: "" });
  const [domainForm, setDomainForm] = useState({ subdomain: "", custom_domain: "", app_link: "" });
  const [templateForm, setTemplateForm] = useState({ template_id: "classic", color_primary: "#185FA5", color_accent: "#1D9E75", color_nav: "#0C447C", panel_color_primary: "#185FA5", panel_color_accent: "#1D9E75", panel_color_nav: "#0C447C", headline: "", tagline: "" });
  const [templatesList, setTemplatesList] = useState([]);
  const [sslStatus, setSslStatus] = useState(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [dnsVerifying, setDnsVerifying] = useState(false);
  const [sslExpanded, setSslExpanded] = useState(true);

  const load = () => {
    const params = new URLSearchParams({ page, limit: PAGE_SIZE });
    if (statusFilter) params.append("status_filter", statusFilter);
    api.get(`/superadmin/admins?${params.toString()}`).then(r => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };
  const loadTemplates = () => api.get("/superadmin/templates").then(r => setTemplatesList(r.data)).catch(() => { });

  useEffect(() => { load(); }, [page, statusFilter]);
  useEffect(() => { loadTemplates(); }, []);

  const approve = async (id) => {
    try { await api.post(`/superadmin/admins/${id}/approve`, { plan: "annual" }); toast.success("Approved + site provisioned"); load(); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : "Approval failed"); }
  };
  const openReject = (a) => { setTarget(a); setRejectForm({ note: "" }); setModal("reject"); };
  const saveReject = async (e) => {
    e.preventDefault();
    if (!rejectForm.note) return toast.error("Please provide a rejection reason");
    try { await api.post(`/superadmin/admins/${target.id}/reject`, { note: rejectForm.note }); toast.success("Rejected"); setModal(null); load(); }
    catch { toast.error("Rejection failed"); }
  };

  const openAdd = () => { setAddForm({ name: "", email: "", mobile: "", password: "", business_name: "", plan: "annual", template_id: "classic", color_primary: "#185FA5", color_accent: "#1D9E75", color_nav: "#0C447C", panel_color_primary: "#185FA5", panel_color_accent: "#1D9E75", panel_color_nav: "#0C447C", headline: "", tagline: "" }); setModal("add"); };
  const openEdit = (a) => { setTarget(a); setEditForm({ business_name: a.business_name || "", contact_person: a.contact_person || "", mobile: a.mobile || "", email: a.email || "" }); setModal("edit"); };
  const openPw = (a) => { setTarget(a); setPwForm({ new_password: "" }); setModal("password"); };
  const openDomain = (a) => { setTarget(a); setDomainForm({ subdomain: a.site?.subdomain || "", custom_domain: a.site?.custom_domain || "", app_link: a.site?.app_link || "" }); setSslStatus(null); setSslExpanded(true); setModal("domain"); loadSSLStatus(a.id); };
  const loadSSLStatus = async (adminId) => { try { const { data } = await api.get(`/superadmin/admins/${adminId}/ssl`); setSslStatus(data); } catch { setSslStatus(null); } };
  const issueSSL = async (domain, domainType) => { setSslLoading(true); try { await api.post(`/superadmin/admins/${target.id}/ssl/issue`, { domain, domain_type: domainType }); toast.success('SSL enabled — Caddy will auto-provision the certificate'); loadSSLStatus(target.id); } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to enable SSL'); } finally { setSslLoading(false); } };
  const verifyDNS = async (domain) => { setDnsVerifying(true); try { const { data } = await api.post(`/superadmin/admins/${target.id}/ssl/verify-dns`, { domain }); if (data.verified) toast.success('DNS verified — domain is correctly configured'); else toast.error(data.message || 'DNS not pointing to dishlco.com'); loadSSLStatus(target.id); } catch (err) { toast.error('DNS verification failed'); } finally { setDnsVerifying(false); } };
  const openTemplate = (a) => {
    setTarget(a);
    setTemplateForm({
      template_id: a.site?.template_id || "classic",
      color_primary: a.site?.color_primary || "#185FA5",
      color_accent: a.site?.color_accent || "#1D9E75",
      color_nav: a.site?.color_nav || "#0C447C",
      panel_color_primary: a.site?.panel_color_primary || a.site?.color_primary || "#185FA5",
      panel_color_accent: a.site?.panel_color_accent || a.site?.color_accent || "#1D9E75",
      panel_color_nav: a.site?.panel_color_nav || a.site?.color_nav || "#0C447C",
      headline: a.site?.headline || "",
      tagline: a.site?.tagline || ""
    });
    setModal("template");
  };

  const saveAdd = async (e) => {
    e.preventDefault();
    try { await api.post("/superadmin/admins", addForm); toast.success("Admin created successfully"); setModal(null); load(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : "Failed to create admin"); }
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.put(`/superadmin/admins/${target.id}`, editForm); toast.success("Admin updated"); setModal(null); load(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : "Failed to update"); }
  };
  const savePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.post(`/superadmin/admins/${target.id}/reset-password`, pwForm); toast.success("Password reset successfully"); setModal(null); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : "Failed to reset password"); }
  };
  const saveDomain = async (e) => {
    e.preventDefault();
    try { await api.put(`/superadmin/admins/${target.id}/site-domain`, domainForm); toast.success("Domain updated"); setModal(null); load(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : "Failed to update domain"); }
  };
  const saveTemplate = async (e) => {
    e.preventDefault();
    try { await api.put(`/superadmin/admins/${target.id}/template`, templateForm); toast.success("Template applied"); setModal(null); load(); }
    catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : "Failed to apply template"); }
  };

  const tone = (s) => s === "active" ? "success" : s === "pending" ? "warn" : s === "suspended" ? "danger" : "neutral";

  const TemplateComponent = TEMPLATES_COMPONENTS[addForm.template_id] || TemplateClassic;
  const previewSite = {
    brand_name: addForm.business_name || "Business Name",
    color_primary: addForm.color_primary,
    color_accent: addForm.color_accent,
    color_nav: addForm.color_nav,
    phone: addForm.mobile || "9876543210",
    email: addForm.email || "demo@example.com",
    headline: addForm.headline,
    tagline: addForm.tagline,
    products: [
      { id: 1, name: "Basic Pack", sell_price: "250", validity_days: 30, category: "D2H" },
      { id: 2, name: "Family Pack", sell_price: "450", validity_days: 30, category: "Tata Play" }
    ]
  };

  const TemplateComponentEdit = TEMPLATES_COMPONENTS[templateForm.template_id] || TemplateClassic;
  const previewSiteEdit = {
    brand_name: target?.business_name || "Business Name",
    color_primary: templateForm.color_primary,
    color_accent: templateForm.color_accent,
    color_nav: templateForm.color_nav,
    phone: target?.mobile || "9876543210",
    email: target?.email || "demo@example.com",
    headline: templateForm.headline,
    tagline: templateForm.tagline,
    products: [
      { id: 1, name: "Basic Pack", sell_price: "250", validity_days: 30, category: "D2H" },
      { id: 2, name: "Family Pack", sell_price: "450", validity_days: 30, category: "Tata Play" }
    ]
  };

  const previewTheme = {
    '--brand-primary': addForm.color_primary,
    '--brand-primary-hover': darkenHex(addForm.color_primary),
    '--brand-accent': addForm.color_accent,
    '--brand-accent-hover': darkenHex(addForm.color_accent),
    '--brand-nav': addForm.color_nav,
    '--brand-nav-hover': darkenHex(addForm.color_nav),
  };

  const previewThemeEdit = {
    '--brand-primary': templateForm.color_primary,
    '--brand-primary-hover': darkenHex(templateForm.color_primary),
    '--brand-accent': templateForm.color_accent,
    '--brand-accent-hover': darkenHex(templateForm.color_accent),
    '--brand-nav': templateForm.color_nav,
    '--brand-nav-hover': darkenHex(templateForm.color_nav),
  };



  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div><h3 className="font-heading font-bold text-2xl text-brand-nav">All LCOs / Admins</h3><p className="text-slate-500 mt-1">Manage admin accounts, domains, and credentials.</p></div>
        <div className="flex items-center gap-4">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20 bg-white">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="cancelled">Rejected</option>
          </select>
          <button onClick={openAdd} data-testid="sa-add-admin" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-sm transition-colors"><Plus className="w-4 h-4" /> Add Admin</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="sa-admins-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-6 py-3">Business</th><th className="text-left px-6 py-3">Contact</th><th className="text-left px-6 py-3">Site</th><th className="text-left px-6 py-3">Status</th><th className="text-right px-6 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`sa-admin-row-${a.id}`}>
                <td className="px-6 py-4"><div className="font-semibold text-brand-nav">{a.business_name}</div><div className="text-xs text-slate-500 font-mono">{a.id.slice(0, 8)}</div></td>
                <td className="px-6 py-4"><div className="text-slate-700">{a.contact_person}</div><div className="text-xs text-slate-500">{a.mobile || a.email || '—'}</div></td>
                <td className="px-6 py-4">
                  {a.site ? (
                    <div className="space-y-1">
                      <a href={`/site/${a.site.subdomain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-xs text-brand-primary hover:underline">
                        <Eye className="w-3 h-3" />{a.site.subdomain}.dishlco.com
                      </a>
                      {a.site.custom_domain && <div className="text-[10px] text-slate-400 font-mono">↳ {a.site.custom_domain}</div>}
                    </div>
                  ) : <span className="text-slate-400 text-xs">Not provisioned</span>}
                </td>
                <td className="px-6 py-4"><Badge tone={tone(a.status)}>{a.status}</Badge></td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex gap-1.5 flex-wrap justify-end">
                    {a.status === "pending" && (<>
                      <button onClick={() => approve(a.id)} data-testid={`approve-${a.id}`} className="inline-flex items-center gap-1 bg-brand-accent hover:bg-brand-accentHover text-white text-xs font-semibold px-3 py-1.5 rounded-md"><CheckCircle2 className="w-3 h-3" /> Approve</button>
                      <button onClick={() => openReject(a)} data-testid={`reject-${a.id}`} className="inline-flex items-center gap-1 border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold px-3 py-1.5 rounded-md"><XCircle className="w-3 h-3" /> Reject</button>
                    </>)}
                    <button onClick={() => openEdit(a)} title="Edit" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:bg-brand-primary/10 px-2 py-1.5 rounded-md transition-colors"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => openPw(a)} title="Reset Password" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:bg-amber-50 px-2 py-1.5 rounded-md transition-colors"><Lock className="w-3 h-3" /></button>
                    {a.site && (
                      <>
                        <button onClick={() => openDomain(a)} title="Edit Domain" className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 px-2 py-1.5 rounded-md transition-colors"><Globe className="w-3 h-3" /></button>
                        <button onClick={() => openTemplate(a)} title="Change Theme" className="inline-flex items-center gap-1 text-xs font-semibold text-pink-600 hover:bg-pink-50 px-2 py-1.5 rounded-md transition-colors"><Palette className="w-3 h-3" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
      </div>

      {/* Add Admin Modal */}
      {modal === "add" && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={saveAdd} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-6xl w-full shadow-xl flex flex-col max-h-[90vh]">
            <h4 className="font-heading font-bold text-xl text-brand-nav shrink-0">Add New Admin & Site Setup</h4>
            <p className="text-sm text-slate-500 mt-1 shrink-0">Create admin account and configure their white-label portal.</p>

            <div className="mt-6 flex-1 min-h-0 flex flex-col md:flex-row gap-6 overflow-hidden">
              {/* Left Form */}
              <div className="flex-1 overflow-y-auto pr-2 space-y-6">
                <div>
                  <h5 className="font-semibold text-slate-800 mb-3 border-b pb-2">Business Details</h5>
                  <div className="space-y-4">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Business Name</label><input required value={addForm.business_name} onChange={e => setAddForm(p => ({ ...p, business_name: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Skylink DTH Services" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Person</label><input required value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="Full name" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number *</label><input required type="tel" value={addForm.mobile} onChange={e => setAddForm(p => ({ ...p, mobile: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="9812345678" /></div>
                      <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Email (Optional)</label><input type="email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="admin@example.com" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label><input required type="password" value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="Min 6 chars" /></div>
                      <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Plan</label><select value={addForm.plan} onChange={e => setAddForm(p => ({ ...p, plan: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"><option value="monthly">Monthly (₹999)</option><option value="half_yearly">Half Yearly (₹4,999)</option><option value="annual">Annual (₹8,999)</option></select></div>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 className="font-semibold text-slate-800 mb-3 border-b pb-2">White-Label Theme</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Template</label>
                      <select value={addForm.template_id} onChange={e => setAddForm(p => ({ ...p, template_id: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                        {templatesList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 gap-4 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Public Site Colors</div>
                      <div className="grid grid-cols-3 gap-4">
                        {TEMPLATE_COLOR_LABELS[addForm.template_id]?.p && (
                          <div>
                            <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">{TEMPLATE_COLOR_LABELS[addForm.template_id].p}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={addForm.color_primary} onChange={e => setAddForm(p => ({ ...p, color_primary: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                              <span className="text-[10px] font-mono">{addForm.color_primary}</span>
                            </div>
                          </div>
                        )}
                        {TEMPLATE_COLOR_LABELS[addForm.template_id]?.a && (
                          <div>
                            <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">{TEMPLATE_COLOR_LABELS[addForm.template_id].a}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={addForm.color_accent} onChange={e => setAddForm(p => ({ ...p, color_accent: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                              <span className="text-[10px] font-mono">{addForm.color_accent}</span>
                            </div>
                          </div>
                        )}
                        {TEMPLATE_COLOR_LABELS[addForm.template_id]?.n && (
                          <div>
                            <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">{TEMPLATE_COLOR_LABELS[addForm.template_id].n}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={addForm.color_nav} onChange={e => setAddForm(p => ({ ...p, color_nav: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                              <span className="text-[10px] font-mono">{addForm.color_nav}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 bg-indigo-50/50 p-3 rounded-lg border border-indigo-100/50">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-semibold text-indigo-800 uppercase tracking-wider">Dashboard Panel Colors</div>
                        <div className="flex gap-1 items-center">
                          <div style={{ backgroundColor: addForm.panel_color_nav }} className="w-4 h-6 rounded-sm border border-slate-300 flex items-start p-0.5"><div style={{ backgroundColor: addForm.panel_color_primary }} className="w-full h-1 rounded-full"></div></div>
                          <div style={{ backgroundColor: '#f8fafc', borderColor: addForm.panel_color_accent }} className="w-6 h-6 rounded-sm border-2 flex items-center justify-center"><div style={{ backgroundColor: addForm.panel_color_accent }} className="w-3 h-1 rounded-full"></div></div>
                          <span className="text-[10px] text-indigo-600 font-medium ml-1">Panel Preview</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">Primary Brand</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={addForm.panel_color_primary} onChange={e => setAddForm(p => ({ ...p, panel_color_primary: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                            <span className="text-[10px] font-mono">{addForm.panel_color_primary}</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">Accent</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={addForm.panel_color_accent} onChange={e => setAddForm(p => ({ ...p, panel_color_accent: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                            <span className="text-[10px] font-mono">{addForm.panel_color_accent}</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">Sidebar / Nav</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={addForm.panel_color_nav} onChange={e => setAddForm(p => ({ ...p, panel_color_nav: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                            <span className="text-[10px] font-mono">{addForm.panel_color_nav}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Headline <span className="text-slate-400 font-normal">(optional)</span></label><input value={addForm.headline} onChange={e => setAddForm(p => ({ ...p, headline: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Recharge Your DTH in Seconds" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Tagline <span className="text-slate-400 font-normal">(optional)</span></label><textarea rows={2} value={addForm.tagline} onChange={e => setAddForm(p => ({ ...p, tagline: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Quick, secure, and reliable DTH recharge..." /></div>
                  </div>
                </div>
              </div>

              {/* Right Live Preview */}
              <div className="flex-[1.5] border border-slate-200 rounded-xl overflow-hidden bg-slate-100 flex flex-col relative min-h-[400px]">
                <div className="bg-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-400"></div><div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div><div className="w-2.5 h-2.5 rounded-full bg-green-400"></div></div>
                  Live Preview
                </div>
                <div className="flex-1 overflow-y-auto overflow-x-hidden relative">
                  <div className="absolute top-0 left-0 w-[133.33%] h-[133.33%] pointer-events-none" style={{ transform: 'scale(0.75)', transformOrigin: 'top left', ...previewTheme }}>
                    <TemplateComponent site={previewSite} />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end gap-3 shrink-0">
              <button type="button" onClick={() => setModal(null)} className="border border-slate-200 text-slate-600 rounded-md px-6 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="bg-brand-accent hover:bg-brand-accentHover text-white rounded-md px-6 py-2 font-semibold transition-colors">Create Admin</button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Admin Modal */}
      {modal === "edit" && target && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={saveEdit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-xl">
            <h4 className="font-heading font-bold text-xl text-brand-nav">Edit Admin — {target.business_name}</h4>
            <div className="mt-6 space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Business Name</label><input value={editForm.business_name} onChange={e => setEditForm(p => ({ ...p, business_name: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Person</label><input value={editForm.contact_person} onChange={e => setEditForm(p => ({ ...p, contact_person: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile *</label><input required type="tel" value={editForm.mobile} onChange={e => setEditForm(p => ({ ...p, mobile: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Email (Optional)</label><input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-accent hover:bg-brand-accentHover text-white rounded-md px-4 py-2 font-semibold transition-colors">Save Changes</button>
            </div>
          </form>
        </div>
      )}

      {/* Reset Password Modal */}
      {modal === "password" && target && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={savePw} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-xl">
            <h4 className="font-heading font-bold text-xl text-brand-nav">Reset Password</h4>
            <p className="text-sm text-slate-500 mt-1">Set a new password for <strong>{target.business_name}</strong></p>
            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
              <input required type="password" minLength={6} value={pwForm.new_password} onChange={e => setPwForm({ new_password: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="Min 6 characters" />
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 font-semibold transition-colors">Reset Password</button>
            </div>
          </form>
        </div>
      )}

      {/* Reject Modal */}
      {modal === "reject" && target && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={saveReject} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <h4 className="font-heading font-bold text-xl text-red-600 flex items-center gap-2"><XCircle className="w-6 h-6" /> Reject Admin Application</h4>
            <p className="text-sm text-slate-500 mt-2">You are about to reject the application for <strong>{target.business_name}</strong>.</p>
            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason for Rejection *</label>
              <textarea required value={rejectForm.note} onChange={e => setRejectForm({ note: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500/20" placeholder="e.g. Incomplete business details..." rows={3} />
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 font-semibold transition-colors">Confirm Rejection</button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Domain Modal */}
      {modal === "domain" && target && (() => {
        const subCert = sslStatus?.subdomain;
        const cdCert = sslStatus?.custom_domain;
        const sslBadge = (cert) => {
          if (!cert) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Not Configured</span>;
          const s = cert.status;
          if (s === 'active') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active (Auto-Managed)</span>;
          if (s === 'pending_dns') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3" /> DNS Pending</span>;
          if (s === 'pending') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Loader2 className="w-3 h-3 animate-spin" /> Provisioning…</span>;
          if (s === 'failed') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Failed</span>;
          return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{s}</span>;
        };
        return (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
              <h4 className="font-heading font-bold text-xl text-brand-nav">Edit Domain & SSL — {target.business_name}</h4>
              <p className="text-sm text-slate-500 mt-1">Configure domains and SSL. Certificates are auto-managed by Caddy.</p>

              <form onSubmit={saveDomain}>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Subdomain</label>
                    <div className="flex items-center"><input required value={domainForm.subdomain} onChange={e => setDomainForm(p => ({ ...p, subdomain: e.target.value }))} className="flex-1 border border-slate-200 rounded-l-md px-3 py-2 text-sm font-mono" placeholder="skylink" /><span className="bg-slate-100 border border-l-0 border-slate-200 text-slate-500 text-sm px-3 py-2 rounded-r-md">.dishlco.com</span></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Custom Domain <span className="text-slate-400 font-normal">(optional)</span></label>
                    <input value={domainForm.custom_domain} onChange={e => setDomainForm(p => ({ ...p, custom_domain: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" placeholder="recharge.skylinkdth.com" />
                    <p className="text-xs text-slate-400 mt-1">DNS CNAME must point to dishlco.com for this to work.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile App Link <span className="text-slate-400 font-normal">(optional)</span></label>
                    <input value={domainForm.app_link} onChange={e => setDomainForm(p => ({ ...p, app_link: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="https://play.google.com/store/apps/details?id=com.example.app" />
                    <p className="text-xs text-slate-400 mt-1">Included in welcome and password reset messages.</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-4 py-2 font-semibold transition-colors">Save Domain</button>
                </div>
              </form>

              {/* ── SSL Management Section ── */}
              <div className="mt-6 border-t pt-5">
                <button type="button" onClick={() => setSslExpanded(!sslExpanded)} className="w-full flex items-center justify-between group">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-left">
                      <h5 className="font-semibold text-sm text-slate-800">SSL Certificate Management</h5>
                      <p className="text-[11px] text-slate-400">Caddy Auto-TLS · Let's Encrypt · Fully Automatic</p>
                    </div>
                  </div>
                  {sslExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>

                {sslExpanded && (
                  <div className="mt-4 space-y-4">
                    {/* Subdomain SSL */}
                    <div className="bg-gradient-to-r from-slate-50 to-blue-50/50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-semibold text-slate-700">Subdomain SSL</span>
                        </div>
                        {sslBadge(subCert)}
                      </div>
                      <p className="text-xs text-slate-500 mb-3 font-mono">{domainForm.subdomain || '—'}.dishlco.com</p>
                      {subCert?.covered_by === 'wildcard' && (
                        <div className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5" /> Covered by wildcard certificate (*.dishlco.com) — auto-managed by Caddy
                        </div>
                      )}
                      {subCert?.note && (
                        <div className="text-[11px] text-slate-500 mb-3">{subCert.note}</div>
                      )}
                      {subCert?.auto_renew && (
                        <div className="text-[11px] text-teal-600 bg-teal-50 border border-teal-100 rounded-lg px-3 py-1.5 mb-3 flex items-center gap-1.5">
                          <RefreshCw className="w-3 h-3" /> Auto-renewal enabled — Caddy handles certificate lifecycle automatically
                        </div>
                      )}
                      <div className="flex gap-2">
                        {(!subCert || subCert.status === 'failed') && (
                          <button type="button" disabled={sslLoading} onClick={() => issueSSL(`${domainForm.subdomain}.dishlco.com`, 'subdomain')}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                            {sslLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Enable SSL
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Custom Domain SSL */}
                    {domainForm.custom_domain && (
                      <div className="bg-gradient-to-r from-slate-50 to-purple-50/50 border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-semibold text-slate-700">Custom Domain SSL</span>
                          </div>
                          {sslBadge(cdCert)}
                        </div>
                        <p className="text-xs text-slate-500 mb-3 font-mono">{domainForm.custom_domain}</p>

                        {/* DNS Verification */}
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[11px] font-medium text-slate-600">DNS Verification</span>
                            {cdCert?.dns_verified ? (
                              <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">✓ Verified</span>
                            ) : (
                              <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Not Verified</span>
                            )}
                          </div>
                          <button type="button" disabled={dnsVerifying} onClick={() => verifyDNS(domainForm.custom_domain)}
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-purple-700 border border-purple-200 hover:bg-purple-50 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50">
                            {dnsVerifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanSearch className="w-3 h-3" />} Verify DNS
                          </button>
                          <p className="text-[10px] text-slate-400 mt-1">CNAME must point to dishlco.com — Caddy will auto-provision the SSL certificate</p>
                        </div>

                        {cdCert?.note && (
                          <div className="text-[11px] text-slate-500 mb-3">{cdCert.note}</div>
                        )}
                        {cdCert?.status === 'active' && cdCert?.auto_renew && (
                          <div className="text-[11px] text-teal-600 bg-teal-50 border border-teal-100 rounded-lg px-3 py-1.5 mb-3 flex items-center gap-1.5">
                            <RefreshCw className="w-3 h-3" /> Auto-renewal enabled — Caddy handles certificate lifecycle automatically
                          </div>
                        )}
                        <div className="flex gap-2">
                          {(!cdCert || cdCert.status === 'failed' || cdCert.status === 'pending_dns') && (
                            <button type="button" disabled={sslLoading} onClick={() => issueSSL(domainForm.custom_domain, 'custom_domain')}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                              {sslLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Enable SSL
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Caddy auto-TLS hint */}
                    <div className="text-[11px] text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                      <span>All SSL certificates are auto-managed by <strong>Caddy</strong> — subdomains are covered by the wildcard cert, custom domains are provisioned on first visit via On-Demand TLS.</span>
                    </div>

                    {!sslStatus && <div className="text-xs text-slate-400 text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading SSL status…</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Template Picker Modal */}
      {modal === "template" && target && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={saveTemplate} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-6xl w-full shadow-xl flex flex-col max-h-[90vh]">
            <h4 className="font-heading font-bold text-xl text-brand-nav shrink-0">Edit Theme & Content — {target.business_name}</h4>
            <p className="text-sm text-slate-500 mt-1 shrink-0">Customize the visual template, colors, and landing page content.</p>

            <div className="mt-6 flex-1 min-h-0 flex flex-col md:flex-row gap-6 overflow-hidden">
              {/* Left Form */}
              <div className="flex-1 overflow-y-auto pr-2 space-y-6">
                <div>
                  <h5 className="font-semibold text-slate-800 mb-3 border-b pb-2">White-Label Theme</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Template</label>
                      <select value={templateForm.template_id} onChange={e => setTemplateForm(p => ({ ...p, template_id: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                        {templatesList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 gap-4 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Public Site Colors</div>
                      <div className="grid grid-cols-3 gap-4">
                        {TEMPLATE_COLOR_LABELS[templateForm.template_id]?.p && (
                          <div>
                            <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">{TEMPLATE_COLOR_LABELS[templateForm.template_id].p}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={templateForm.color_primary} onChange={e => setTemplateForm(p => ({ ...p, color_primary: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                              <span className="text-[10px] font-mono">{templateForm.color_primary}</span>
                            </div>
                          </div>
                        )}
                        {TEMPLATE_COLOR_LABELS[templateForm.template_id]?.a && (
                          <div>
                            <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">{TEMPLATE_COLOR_LABELS[templateForm.template_id].a}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={templateForm.color_accent} onChange={e => setTemplateForm(p => ({ ...p, color_accent: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                              <span className="text-[10px] font-mono">{templateForm.color_accent}</span>
                            </div>
                          </div>
                        )}
                        {TEMPLATE_COLOR_LABELS[templateForm.template_id]?.n && (
                          <div>
                            <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">{TEMPLATE_COLOR_LABELS[templateForm.template_id].n}</label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={templateForm.color_nav} onChange={e => setTemplateForm(p => ({ ...p, color_nav: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                              <span className="text-[10px] font-mono">{templateForm.color_nav}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 bg-indigo-50/50 p-3 rounded-lg border border-indigo-100/50">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-semibold text-indigo-800 uppercase tracking-wider">Dashboard Panel Colors</div>
                        <div className="flex gap-1 items-center">
                          <div style={{ backgroundColor: templateForm.panel_color_nav }} className="w-4 h-6 rounded-sm border border-slate-300 flex items-start p-0.5"><div style={{ backgroundColor: templateForm.panel_color_primary }} className="w-full h-1 rounded-full"></div></div>
                          <div style={{ backgroundColor: '#f8fafc', borderColor: templateForm.panel_color_accent }} className="w-6 h-6 rounded-sm border-2 flex items-center justify-center"><div style={{ backgroundColor: templateForm.panel_color_accent }} className="w-3 h-1 rounded-full"></div></div>
                          <span className="text-[10px] text-indigo-600 font-medium ml-1">Panel Preview</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">Primary Brand</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={templateForm.panel_color_primary} onChange={e => setTemplateForm(p => ({ ...p, panel_color_primary: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                            <span className="text-[10px] font-mono">{templateForm.panel_color_primary}</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">Accent</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={templateForm.panel_color_accent} onChange={e => setTemplateForm(p => ({ ...p, panel_color_accent: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                            <span className="text-[10px] font-mono">{templateForm.panel_color_accent}</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1 h-4">Sidebar / Nav</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={templateForm.panel_color_nav} onChange={e => setTemplateForm(p => ({ ...p, panel_color_nav: e.target.value }))} className="w-8 h-8 rounded cursor-pointer" />
                            <span className="text-[10px] font-mono">{templateForm.panel_color_nav}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Headline <span className="text-slate-400 font-normal">(optional)</span></label><input value={templateForm.headline} onChange={e => setTemplateForm(p => ({ ...p, headline: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Recharge Your DTH in Seconds" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Tagline <span className="text-slate-400 font-normal">(optional)</span></label><textarea rows={2} value={templateForm.tagline} onChange={e => setTemplateForm(p => ({ ...p, tagline: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Quick, secure, and reliable DTH recharge..." /></div>
                  </div>
                </div>
              </div>

              {/* Right Live Preview */}
              <div className="flex-[1.5] border border-slate-200 rounded-xl overflow-hidden bg-slate-100 flex flex-col relative min-h-[400px]">
                <div className="bg-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-400"></div><div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div><div className="w-2.5 h-2.5 rounded-full bg-green-400"></div></div>
                  Live Preview
                </div>
                <div className="flex-1 overflow-y-auto overflow-x-hidden relative">
                  <div className="absolute top-0 left-0 w-[133.33%] h-[133.33%] pointer-events-none" style={{ transform: 'scale(0.75)', transformOrigin: 'top left', ...previewThemeEdit }}>
                    <TemplateComponentEdit site={previewSiteEdit} />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end gap-3 shrink-0">
              <button type="button" onClick={() => setModal(null)} className="border border-slate-200 text-slate-600 rounded-md px-6 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white rounded-md px-6 py-2 font-semibold transition-colors">Apply Theme</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


function Gateways() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null); // null | 'add' | gateway obj for edit
  const [form, setForm] = useState({ gateway_name: "", display_name: "", webhook_path: "", callback_path: "", is_enabled: true, required_fields: [] });
  const load = () => api.get("/superadmin/gateways").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  // ── Platform Razorpay Config ──────────────────────────────
  const [platConfig, setPlatConfig] = useState(null);
  const [platForm, setPlatForm] = useState({ key_id: "", key_secret: "", webhook_secret: "" });
  const [platSaving, setPlatSaving] = useState(false);
  const [platTesting, setPlatTesting] = useState(false);
  const [showPlatSecrets, setShowPlatSecrets] = useState(false);

  const loadPlatConfig = async () => {
    try {
      const { data } = await api.get("/superadmin/razorpay/platform-config");
      setPlatConfig(data);
      if (data.configured) {
        setPlatForm({ key_id: data.key_id || "", key_secret: "", webhook_secret: "" });
      }
    } catch { }
  };
  useEffect(() => { loadPlatConfig(); }, []);

  const savePlatConfig = async (e) => {
    e.preventDefault();
    if (!platForm.key_id) { toast.error("Key ID is required"); return; }
    setPlatSaving(true);
    try {
      const payload = { key_id: platForm.key_id };
      // Only send secret fields if user typed something (not empty = not changing)
      payload.key_secret = platForm.key_secret || (platConfig?.key_secret ? undefined : "");
      if (platForm.key_secret) payload.key_secret = platForm.key_secret;
      else if (!platConfig?.configured) { toast.error("Key Secret is required"); setPlatSaving(false); return; }
      else {
        // Fetch existing and keep it - but we can't, so require it
        // Actually for update we need to re-send... let's require it
        toast.error("Please re-enter the Key Secret to save changes");
        setPlatSaving(false);
        return;
      }
      payload.webhook_secret = platForm.webhook_secret || null;
      await api.put("/superadmin/razorpay/platform-config", payload);
      toast.success("Platform Razorpay config saved!");
      loadPlatConfig();
      setPlatForm(p => ({ ...p, key_secret: "", webhook_secret: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save config");
    } finally { setPlatSaving(false); }
  };

  const testPlatCredentials = async () => {
    setPlatTesting(true);
    try {
      const { data } = await api.post("/superadmin/razorpay/test-platform-credentials");
      if (data.ok) toast.success(data.message || "Platform credentials are valid ✓");
      else toast.error(data.message || "Invalid credentials");
    } catch { toast.error("Test failed"); }
    finally { setPlatTesting(false); }
  };

  const toggle = async (id) => { try { await api.post(`/superadmin/gateways/${id}/toggle`); toast.success("Toggled"); load(); } catch { toast.error("Failed"); } };

  const openAdd = () => { setForm({ gateway_name: "", display_name: "", webhook_path: "", callback_path: "", is_enabled: true, required_fields: [] }); setModal("add"); };
  const openEdit = (g) => { setForm({ gateway_name: g.gateway_name, display_name: g.display_name, webhook_path: g.webhook_path, callback_path: g.callback_path, is_enabled: g.is_enabled, required_fields: g.required_fields || [] }); setModal(g); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal === "add") {
        await api.post("/superadmin/gateways", form);
        toast.success("Gateway created");
      } else {
        await api.put(`/superadmin/gateways/${modal.id}`, form);
        toast.success("Gateway updated");
      }
      setModal(null); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to save gateway");
    }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div>
      {/* ── Platform Razorpay Config ──────────────────────── */}
      <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-xl text-indigo-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              Platform Razorpay Config
            </h3>
            <p className="text-sm text-indigo-600/70 mt-0.5">Central Razorpay credentials for Route transfers & webhooks. Shared across all admins.</p>
          </div>
          {platConfig?.configured && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Configured
            </span>
          )}
        </div>

        <form onSubmit={savePlatConfig} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-indigo-800 mb-1">Razorpay Key ID</label>
              <input required value={platForm.key_id} onChange={e => setPlatForm(p => ({ ...p, key_id: e.target.value }))}
                className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                placeholder="rzp_test_XXXXXXXXXX" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-800 mb-1">Razorpay Key Secret</label>
              <input required={!platConfig?.configured} value={platForm.key_secret}
                onChange={e => setPlatForm(p => ({ ...p, key_secret: e.target.value }))}
                type={showPlatSecrets ? "text" : "password"}
                className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                placeholder={platConfig?.configured ? "(re-enter to update)" : "your_key_secret"} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-800 mb-1">Webhook Secret</label>
              <input value={platForm.webhook_secret} onChange={e => setPlatForm(p => ({ ...p, webhook_secret: e.target.value }))}
                type={showPlatSecrets ? "text" : "password"}
                className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                placeholder={platConfig?.webhook_secret ? "(set — re-enter to change)" : "optional webhook secret"} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-indigo-700 cursor-pointer select-none">
              <input type="checkbox" checked={showPlatSecrets} onChange={e => setShowPlatSecrets(e.target.checked)} className="rounded" />
              Show secrets
            </label>
            <div className="flex gap-2">
              {platConfig?.configured && (
                <button type="button" onClick={testPlatCredentials} disabled={platTesting}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 border border-indigo-200 hover:bg-indigo-100 px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                  {platTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
                  Test Credentials
                </button>
              )}
              <button type="submit" disabled={platSaving}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-5 py-2 rounded-lg transition-colors disabled:opacity-50">
                {platSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Save Platform Config
              </button>
            </div>
          </div>
          {platConfig?.configured && (
            <div className="text-xs text-indigo-500 mt-1">
              Current Key: <span className="font-mono">{platConfig.key_id}</span>
              {platConfig.key_secret && <> · Secret: <span className="font-mono">{platConfig.key_secret}</span></>}
              {platConfig.webhook_secret && <> · Webhook: <span className="font-mono">{platConfig.webhook_secret}</span></>}
              {platConfig.updated_at && <> · Updated: {platConfig.updated_at.slice(0, 19).replace('T', ' ')}</>}
            </div>
          )}
        </form>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Payment Gateway Registry</h3>
          <p className="text-slate-500 mt-1">Add, edit, enable/disable gateways platform-wide. Configure per-admin credentials in Admin GW Configs.</p>
        </div>
        <button onClick={openAdd} data-testid="sa-add-gateway" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-sm transition-colors">
          <Plus className="w-4 h-4" /> Add Gateway
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((g) => (
          <div key={g.id} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-testid={`gateway-card-${g.gateway_name}`}>
            <div className="flex items-start justify-between">
              <div className="font-heading font-bold text-lg text-brand-nav">{g.display_name}</div>
              <button onClick={() => toggle(g.id)} data-testid={`gateway-toggle-${g.gateway_name}`} className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${g.is_enabled ? "bg-brand-accent/10 text-brand-accent hover:bg-brand-accent/20" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}><Power className="w-3 h-3" /> {g.is_enabled ? "Enabled" : "Disabled"}</button>
            </div>
            <div className="mt-1 text-xs font-mono text-slate-400">{g.gateway_name}</div>
            <div className="mt-3 space-y-1 text-xs font-mono text-slate-500"><div>webhook: {g.webhook_path}</div><div>callback: {g.callback_path}</div></div>
            <div className="mt-4 flex flex-wrap gap-1.5">{(g.required_fields || []).map((f) => (<span key={f.field} className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{f.field}</span>))}</div>
            <button onClick={() => openEdit(g)} data-testid={`gateway-edit-${g.gateway_name}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:text-brand-primaryHover transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Edit Details
            </button>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-xl">
            <h4 className="font-heading font-bold text-xl text-brand-nav">{modal === "add" ? "Add New Gateway" : "Edit Gateway"}</h4>
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Gateway Slug</label>
                <input required value={form.gateway_name} onChange={(e) => setField("gateway_name", e.target.value)} disabled={modal !== "add"}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm disabled:bg-slate-50 disabled:text-slate-400" placeholder="e.g. razorpay" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Name</label>
                <input required value={form.display_name} onChange={(e) => setField("display_name", e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Razorpay" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Webhook Path</label>
                <input required value={form.webhook_path} onChange={(e) => setField("webhook_path", e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm" placeholder="/webhooks/razorpay" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Callback Path</label>
                <input required value={form.callback_path} onChange={(e) => setField("callback_path", e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm" placeholder="/callbacks/razorpay" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_enabled} onChange={(e) => setField("is_enabled", e.target.checked)} id="gw-enabled" className="rounded" />
                <label htmlFor="gw-enabled" className="text-sm font-medium text-slate-700">Enabled</label>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-accent hover:bg-brand-accentHover text-white rounded-md px-4 py-2 font-semibold transition-colors">{modal === "add" ? "Create" : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function AdminGatewayConfigs() {
  const [configs, setConfigs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState({ admin_id: "", platform_gateway_id: "", gateway_mode: "linked_account", linked_account_id: "", personal_key_id: "", personal_key_secret: "", personal_webhook_secret: "", commission_percent: 5.0, gateway_fee_percent: 0.0, gateway_fee_bearer: "user", is_active: true });
  const [showSecret, setShowSecret] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // config being edited
  const [editLoading, setEditLoading] = useState(false);

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const save = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      if (modal === "add") {
        await api.post("/superadmin/gateways/admin-configs/create", form);
        toast.success("Gateway config added");
      } else {
        await api.put(`/superadmin/gateways/admin-configs/${editTarget.id}/update`, form);
        toast.success("Gateway config updated");
      }
      setModal(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : "Failed to save config"));
    } finally {
      setEditLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this gateway config?")) return;
    try { await api.delete(`/superadmin/gateways/admin-configs/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAdmin, setFilterAdmin] = useState("");
  const [filterGateway, setFilterGateway] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // '' | 'active' | 'inactive'

  const clearFilters = () => {
    setSearchQuery("");
    setFilterAdmin("");
    setFilterGateway("");
    setFilterStatus("");
  };

  const hasActiveFilters = searchQuery || filterAdmin || filterGateway || filterStatus;

  const load = useCallback(() => {
    const params = new URLSearchParams({ page, limit: PAGE_SIZE });
    if (searchQuery) params.set("search", searchQuery);
    if (filterAdmin) params.set("admin_id", filterAdmin);
    if (filterGateway) params.set("platform_gateway_id", filterGateway);
    if (filterStatus) params.set("status", filterStatus);

    api.get(`/superadmin/gateways/admin-configs?${params.toString()}`).then(r => {
      setConfigs(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => { });
  }, [page, searchQuery, filterAdmin, filterGateway, filterStatus]);

  useEffect(() => {
    Promise.all([
      api.get("/superadmin/admins/list"),
      api.get("/superadmin/gateways")
    ]).then(([a, g]) => {
      setAdmins(Array.isArray(a.data) ? a.data : a.data.items || []);
      setGateways(g.data);
    }).catch(() => { });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounce search query
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterAdmin, filterGateway, filterStatus]);

  const openAdd = () => {
    setForm({ admin_id: "", platform_gateway_id: "", gateway_mode: "linked_account", linked_account_id: "", personal_key_id: "", personal_key_secret: "", personal_webhook_secret: "", commission_percent: 5.0, gateway_fee_percent: 0.0, gateway_fee_bearer: "user", is_active: true });
    setEditTarget(null);
    setShowSecret(false);
    setModal("add");
  };

  const openEdit = async (config) => {
    setEditTarget(config);
    setEditLoading(true);
    setShowSecret(false);
    setModal("edit");
    try {
      const { data } = await api.get(`/superadmin/gateways/admin-configs/${config.id}/detail`);
      setForm({
        admin_id: data.admin_id,
        platform_gateway_id: data.platform_gateway_id,
        gateway_mode: data.gateway_mode || "linked_account",
        linked_account_id: data.linked_account_id || "",
        personal_key_id: data.personal_key_id || "",
        personal_key_secret: "",
        personal_webhook_secret: "",
        commission_percent: data.commission_percent ?? 5.0,
        gateway_fee_percent: data.gateway_fee_percent ?? 0.0,
        gateway_fee_bearer: data.gateway_fee_bearer || "user",
        is_active: data.is_active ?? true,
      });
      setEditTarget({ ...config, ...data });
    } catch {
      toast.error("Failed to load config details");
      setModal(null);
    } finally {
      setEditLoading(false);
    }
  };

  const testPersonalCredentials = async () => {
    if (!editTarget?.id) return;
    setTestLoading(true);
    try {
      const { data } = await api.post(`/superadmin/gateways/admin-configs/${editTarget.id}/test-personal`);
      if (data.ok) toast.success(`✅ Credentials valid! ${data.message || ''}`);
      else toast.error(`❌ ${data.message || 'Test failed'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setTestLoading(false);
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const getWebhookUrl = () => {
    if (!form.admin_id) return 'Select an admin first...';
    const admin = admins.find(a => a.id === form.admin_id);
    if (!admin) return `${BACKEND_URL}/webhooks/razorpay/personal/${form.admin_id}`;

    let domain = "";
    if (admin.custom_domain) {
      domain = `https://${admin.custom_domain}`;
    } else if (admin.subdomain) {
      const hostParts = window.location.hostname.split('.');
      const baseDomain = hostParts.length > 2 ? hostParts.slice(1).join('.') : hostParts.join('.');
      const finalBase = baseDomain === 'localhost' ? 'dishlco.com' : baseDomain;
      domain = `https://${admin.subdomain}.${finalBase}`;
    } else {
      return `${BACKEND_URL}/webhooks/razorpay/personal/${form.admin_id}`;
    }

    return `${domain}/api/webhooks/razorpay/personal/${form.admin_id}`;
  };

  const onGatewayChange = (gwId) => {
    setForm(prev => ({ ...prev, platform_gateway_id: gwId }));
  };



  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Admin Gateway Configurations</h3>
          <p className="text-slate-500 mt-1">Manage payment gateway credentials for each admin (LCO) centrally.</p>
        </div>
        <button onClick={openAdd} data-testid="sa-add-admin-gw-config" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-sm transition-colors">
          <Plus className="w-4 h-4" /> Add Config
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="mb-5 bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by admin, gateway, or ID..."
                className="w-full border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </div>
          </div>
          {/* Admin Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Admin</label>
            <select value={filterAdmin} onChange={e => setFilterAdmin(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors">
              <option value="">All Admins</option>
              {admins.map(a => (
                <option key={a.id} value={a.id}>{a.business_name}</option>
              ))}
            </select>
          </div>
          {/* Gateway Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Gateway</label>
            <select value={filterGateway} onChange={e => setFilterGateway(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors">
              <option value="">All Gateways</option>
              {gateways.map(g => (
                <option key={g.id} value={g.id}>{g.display_name}</option>
              ))}
            </select>
          </div>
          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors">
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {/* Clear Filters */}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-500 border border-slate-200 hover:border-red-200 hover:bg-red-50 px-3 py-2 rounded-md transition-colors">
              <XCircle className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
        {hasActiveFilters && (
          <div className="mt-2 text-xs text-slate-400">
            Showing {configs.length} of {total} config{total !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-6 py-3">Admin</th>
              <th className="text-left px-6 py-3">Gateway</th>
              <th className="text-left px-6 py-3">Mode</th>
              <th className="text-left px-6 py-3">Account / Key</th>
              <th className="text-left px-6 py-3">Commission</th>
              <th className="text-left px-6 py-3">Gateway Fee</th>
              <th className="text-left px-6 py-3">Fee Bearer</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-right px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-8 text-center text-slate-400">
                {hasActiveFilters ? "No configs match your filters." : "No admin gateway configs yet. Click \"Add Config\" to get started."}
              </td></tr>
            )}
            {configs.map((c, i) => (
              <tr key={c.id || i} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-6 py-4">
                  <div className="font-semibold text-brand-nav">{c.admin_name}</div>
                  <div className="text-xs text-slate-400 font-mono">{(c.admin_id || "").slice(0, 8)}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-slate-700">{c.gateway_display_name}</div>
                  <div className="text-xs text-slate-400 font-mono">{(c.platform_gateway_id || "").slice(0, 8)}</div>
                </td>
                <td className="px-6 py-4">
                  {(c.gateway_mode || 'linked_account') === 'personal'
                    ? <Badge tone="info">🔑 Personal</Badge>
                    : <Badge tone="success">🔗 Route</Badge>}
                </td>
                <td className="px-6 py-4">
                  <div className="font-mono text-xs text-slate-600">
                    {(c.gateway_mode || 'linked_account') === 'personal'
                      ? (c.personal_key_id || '—')
                      : (c.linked_account_id || '—')}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="font-semibold text-brand-primary">{c.commission_percent ?? 5}%</span>
                </td>
                <td className="px-6 py-4">
                  <span className="font-semibold text-slate-700">{c.gateway_fee_percent ?? 0}%</span>
                </td>
                <td className="px-6 py-4">
                  {(c.gateway_fee_bearer || 'user') === 'admin'
                    ? <Badge tone="warn">Admin</Badge>
                    : <Badge tone="info">User</Badge>}
                </td>
                <td className="px-6 py-4">
                  {c.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="warn">Inactive</Badge>}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex gap-1.5 flex-wrap justify-end">
                    <button onClick={() => openEdit(c)} title="Edit Config" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:text-brand-primaryHover hover:bg-brand-primary/10 px-2 py-1.5 rounded-md transition-colors">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors px-2 py-1 rounded hover:bg-red-50">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => { setModal(null); setEditTarget(null); }}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl flex flex-col max-w-lg w-full shadow-xl max-h-[90vh]">
            <div className="p-6 md:p-8 pb-4 border-b border-slate-100 shrink-0">
              <h4 className="font-heading font-bold text-xl text-brand-nav">
                {modal === "edit" ? "Edit Gateway Config" : "Configure Gateway for Admin"}
              </h4>
              <p className="text-sm text-slate-500 mt-1">
                {modal === "edit"
                  ? `Update config for ${editTarget?.admin_name || "admin"}'s ${editTarget?.gateway_display_name || "gateway"}.`
                  : "Select the admin, gateway, and choose between Route (linked account) or Personal gateway mode."
                }
              </p>
            </div>

            <div className="p-6 md:p-8 overflow-y-auto flex-1 custom-scrollbar">
              {editLoading ? (
                <div className="mt-8 flex items-center justify-center gap-2 text-slate-400 py-12">
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading credentials...
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin (LCO)</label>
                      <SearchableSelect
                        disabled={modal === "edit"}
                        value={form.admin_id}
                        onChange={(val) => setForm(prev => ({ ...prev, admin_id: val }))}
                        defaultLabel="Select admin..."
                        options={admins.filter(a => a.status === "active").map(a => ({ label: `${a.business_name} (${a.email})`, value: a.id }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Payment Gateway</label>
                      <select required value={form.platform_gateway_id} onChange={(e) => onGatewayChange(e.target.value)}
                        disabled={modal === "edit"}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500">
                        <option value="">Select gateway...</option>
                        {gateways.filter(g => g.is_enabled).map(g => (
                          <option key={g.id} value={g.id}>{g.display_name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Gateway Mode Selector */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Gateway Mode</label>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className={`flex-1 flex items-center gap-2.5 p-3 border-2 rounded-lg cursor-pointer transition-all ${form.gateway_mode === 'linked_account' ? 'border-brand-primary bg-brand-primary/5' : 'border-slate-200 hover:border-slate-300'}`}>
                          <input type="radio" name="gateway_mode" value="linked_account"
                            checked={form.gateway_mode === 'linked_account'}
                            onChange={() => setForm(prev => ({ ...prev, gateway_mode: 'linked_account' }))}
                            className="accent-brand-primary" />
                          <div>
                            <div className="text-sm font-semibold">🔗 Linked Account (Route)</div>
                            <div className="text-xs text-slate-400">Platform processes payments, splits to admin</div>
                          </div>
                        </label>
                        <label className={`flex-1 flex items-center gap-2.5 p-3 border-2 rounded-lg cursor-pointer transition-all ${form.gateway_mode === 'personal' ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
                          <input type="radio" name="gateway_mode" value="personal"
                            checked={form.gateway_mode === 'personal'}
                            onChange={() => setForm(prev => ({ ...prev, gateway_mode: 'personal' }))}
                            className="accent-blue-500" />
                          <div>
                            <div className="text-sm font-semibold">🔑 Personal Gateway</div>
                            <div className="text-xs text-slate-400">Admin's own Razorpay keys</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Linked Account Mode Fields */}
                    {form.gateway_mode === 'linked_account' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Linked Account ID</label>
                        <input required value={form.linked_account_id} onChange={(e) => setForm(prev => ({ ...prev, linked_account_id: e.target.value }))}
                          className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm" placeholder="acc_XXXXXXXXXXXXXXXX" />
                        <p className="text-xs text-slate-400 mt-1">From Razorpay Dashboard → Route → Linked Accounts</p>
                      </div>
                    )}

                    {/* Personal Mode Fields */}
                    {form.gateway_mode === 'personal' && (
                      <>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-700 font-medium">💡 Personal Gateway Mode</p>
                          <p className="text-xs text-blue-600 mt-1">Payments go directly to admin's Razorpay account. Commission is tracked for offline settlement.</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Razorpay Key ID</label>
                          <input required value={form.personal_key_id} onChange={(e) => setForm(prev => ({ ...prev, personal_key_id: e.target.value }))}
                            className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm" placeholder="rzp_live_XXXXXXXXXXXXXXXX" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Razorpay Key Secret</label>
                          <div className="relative">
                            <input type={showSecret ? 'text' : 'password'}
                              value={form.personal_key_secret}
                              onChange={(e) => setForm(prev => ({ ...prev, personal_key_secret: e.target.value }))}
                              className="w-full border border-slate-200 rounded-md px-3 py-2 pr-20 font-mono text-sm"
                              placeholder={modal === 'edit' ? '(leave empty to keep current)' : 'Enter key secret'}
                              required={modal === 'add'} />
                            <button type="button" onClick={() => setShowSecret(!showSecret)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 px-2 py-1">
                              {showSecret ? 'Hide' : 'Show'}
                            </button>
                          </div>
                          {modal === 'edit' && editTarget?.personal_key_secret_masked && (
                            <p className="text-xs text-slate-400 mt-1">Current: {editTarget.personal_key_secret_masked}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Webhook Secret</label>
                          <input type={showSecret ? 'text' : 'password'}
                            value={form.personal_webhook_secret}
                            onChange={(e) => setForm(prev => ({ ...prev, personal_webhook_secret: e.target.value }))}
                            className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm"
                            placeholder={modal === 'edit' ? '(leave empty to keep current)' : 'Enter webhook secret'} />
                          {modal === 'edit' && editTarget?.has_webhook_secret && (
                            <p className="text-xs text-green-600 mt-1">✓ Webhook secret is configured</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">Webhook URL</label>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <input readOnly value={getWebhookUrl()}
                              className="flex-1 border border-slate-200 rounded-md px-3 py-2 font-mono text-xs bg-slate-50 text-slate-600" />
                            <button type="button" onClick={() => {
                              navigator.clipboard.writeText(getWebhookUrl());
                              toast.success('Webhook URL copied!');
                            }} disabled={!form.admin_id}
                              className="px-3 py-2 text-xs font-semibold text-brand-primary border border-brand-primary/30 rounded-md hover:bg-brand-primary/10 transition-colors disabled:opacity-40">
                              Copy
                            </button>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">Admin must set this URL in their Razorpay Dashboard → Webhooks</p>
                        </div>
                        {modal === 'edit' && editTarget?.gateway_mode === 'personal' && (
                          <button type="button" onClick={testPersonalCredentials} disabled={testLoading}
                            className="w-full border-2 border-dashed border-green-300 text-green-600 font-semibold text-sm py-2 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50">
                            {testLoading ? '⏳ Testing...' : '🧪 Test Personal Credentials'}
                          </button>
                        )}
                      </>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Platform Commission (%)</label>
                      <input required type="number" min="0" max="100" step="0.1" value={form.commission_percent}
                        onChange={(e) => setForm(prev => ({ ...prev, commission_percent: parseFloat(e.target.value) || 0 }))}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="5.0" />
                      <p className="text-xs text-slate-400 mt-1">% of each payment kept by platform. Rest goes to admin.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Gateway Fee (%)</label>
                      <input required type="number" min="0" max="100" step="0.01" value={form.gateway_fee_percent}
                        onChange={(e) => setForm(prev => ({ ...prev, gateway_fee_percent: parseFloat(e.target.value) || 0 }))}
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="2.0" />
                      <p className="text-xs text-slate-400 mt-1">Razorpay gateway fee percentage (e.g. 2% for UPI, 2.36% for cards).</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Fee Bearer</label>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="fee_bearer" value="user" checked={form.gateway_fee_bearer === 'user'}
                            onChange={() => setForm(prev => ({ ...prev, gateway_fee_bearer: 'user' }))}
                            className="accent-brand-primary" />
                          <span className="text-sm">User <span className="text-xs text-slate-400">(Dealer / Customer pays)</span></span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="fee_bearer" value="admin" checked={form.gateway_fee_bearer === 'admin'}
                            onChange={() => setForm(prev => ({ ...prev, gateway_fee_bearer: 'admin' }))}
                            className="accent-brand-primary" />
                          <span className="text-sm">Admin <span className="text-xs text-slate-400">(Deducted from admin's share)</span></span>
                        </label>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Who absorbs the payment gateway processing fee.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))} id="cfg-active" className="rounded" />
                      <label htmlFor="cfg-active" className="text-sm font-medium text-slate-700">Active</label>
                    </div>
                  </div>
                </>
              )}
            </div>

            {!editLoading && (
              <div className="p-6 md:p-8 pt-4 border-t border-slate-100 shrink-0 mt-auto">
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setModal(null); setEditTarget(null); }} className="flex-1 border border-slate-200 text-slate-600 rounded-md px-4 py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 bg-brand-accent hover:bg-brand-accentHover text-white rounded-md px-4 py-2 font-semibold transition-colors">{modal === "edit" ? "Update Config" : "Save Config"}</button>
                </div>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

function TestCredBtn({ adminId }) {
  const [s, setS] = useState(null); // null | 'loading' | 'ok' | 'fail'
  const test = async () => {
    setS('loading');
    try {
      const { data } = await api.post('/superadmin/gateways/razorpay/test-credentials', { admin_id: adminId });
      setS(data.ok ? 'ok' : 'fail');
      toast[data.ok ? 'success' : 'error'](data.message || (data.ok ? 'Valid' : 'Invalid'));
    } catch { setS('fail'); toast.error('Test failed'); }
    setTimeout(() => setS(null), 4000);
  };
  return (
    <button onClick={test} disabled={s === 'loading'} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded transition-colors ${s === 'ok' ? 'text-green-600 bg-green-50' : s === 'fail' ? 'text-red-600 bg-red-50' : 'text-brand-primary hover:bg-brand-primary/10'
      }`}>
      {s === 'loading' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
      {s === 'ok' ? 'Valid ✓' : s === 'fail' ? 'Failed ✗' : 'Test'}
    </button>
  );
}

/* ── SA Transaction helpers ───────────────────────────────────────── */
const SA_TXN_STATUS = {
  success: { label: "Success", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  payment_verified: { label: "Payment Verified", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: ShieldCheck },
  processing: { label: "Processing", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: Loader2 },
  recharge_failed: { label: "Recharge Failed", cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  failed: { label: "Payment Failed", cls: "bg-red-50 text-red-600 border-red-200", Icon: XCircle },
  amount_mismatch: { label: "Amount Mismatch", cls: "bg-orange-50 text-orange-700 border-orange-200", Icon: AlertTriangle },
};
const getSATxnStatus = s => SA_TXN_STATUS[s] || { label: s, cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: Clock };
const SA_TXN_STEP_ICONS = {
  recharge_initiated: { Icon: Zap, c: "text-blue-500" }, razorpay_order_created: { Icon: CreditCard, c: "text-blue-500" },
  razorpay_payment_received: { Icon: ShieldCheck, c: "text-green-500" }, webhook_payment_captured: { Icon: Radio, c: "text-green-600" },
  dishtv_api_request: { Icon: ArrowRight, c: "text-indigo-500" }, dishtv_api_success: { Icon: CheckCircle2, c: "text-emerald-500" },
  dishtv_api_failed: { Icon: XCircle, c: "text-red-500" }, recharge_completed: { Icon: CheckCircle2, c: "text-emerald-600" },
  recharge_failed: { Icon: XCircle, c: "text-red-500" },
};
const getSATxnStep = e => SA_TXN_STEP_ICONS[e] || { Icon: Clock, c: "text-slate-400" };
const fmtSATxn = v => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSATxnDate = d => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

function SATxnPipeline({ steps }) {
  if (!steps?.length) return <p className="text-xs text-slate-400 italic py-2">No pipeline events recorded.</p>;
  return (
    <div className="relative pl-6">
      <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />
      {steps.map((step, i) => {
        const si = getSATxnStep(step.event); const Icon = si.Icon;
        const dot = step.status === 'success' ? 'bg-emerald-500' : step.status === 'error' ? 'bg-red-500' : 'bg-slate-400';
        return (
          <div key={i} className="relative pb-4 last:pb-0">
            <div className={`absolute -left-6 top-1 w-[22px] h-[22px] rounded-full border-2 border-white shadow-sm flex items-center justify-center ${step.status === 'success' ? 'bg-emerald-50' : step.status === 'error' ? 'bg-red-50' : 'bg-slate-50'}`}>
              <Icon className={`w-3 h-3 ${si.c}`} />
            </div>
            <div className="ml-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-700">{step.event_label || step.event}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                <span className="text-[10px] text-slate-400 font-mono">{step.timestamp?.replace('T', ' ').slice(0, 19)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{step.message}</p>
              {step.amount != null && <span className="text-[10px] font-mono text-slate-400">Amount: {fmtSATxn(step.amount)}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SATxnDetailRow({ label, value, mono, highlight, error }) {
  return (
    <div className="flex justify-between items-center px-4 py-2.5">
      <span className="text-slate-400 font-medium text-xs">{label}</span>
      <span className={`font-semibold text-right text-xs ${mono ? 'font-mono text-slate-600' : highlight ? 'text-brand-accent font-bold' : error ? 'text-red-600' : 'text-slate-700'}`}>{value}</span>
    </div>
  );
}

function SATxnRow({ r, isOpen, onToggle }) {
  const s = getSATxnStatus(r.status); const Icon = s.Icon;
  const pipeline = r.pipeline || [];
  const hasPayment = pipeline.some(p => ['razorpay_payment_received', 'webhook_payment_captured'].includes(p.event));
  const dishtvOk = pipeline.some(p => p.event === 'dishtv_api_success');
  const dishtvFail = pipeline.some(p => ['dishtv_api_failed', 'dishtv_api_error'].includes(p.event));
  const hasDishtvCall = pipeline.some(p => p.event === 'dishtv_api_request');
  return (
    <>
      <tr onClick={onToggle} className={`border-t border-slate-100 cursor-pointer transition-colors ${isOpen ? 'bg-indigo-50/40' : 'hover:bg-slate-50/70'}`}>
        <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Hash className="w-3 h-3 text-slate-400 shrink-0" /><span className="font-mono font-bold text-xs text-brand-primary">{r.txn_no || r.id?.slice(0, 8)}</span></div></td>
        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtSATxnDate(r.created_at)}</td>
        <td className="px-4 py-3 text-xs font-semibold text-indigo-700">{r.admin_name || '—'}</td>
        <td className="px-4 py-3"><p className="text-xs font-semibold text-slate-700">{r.customer_name || '—'}</p><p className="text-[10px] font-mono text-slate-400">{r.dth_id || '—'}</p></td>
        <td className="px-4 py-3 text-xs text-slate-600 max-w-[100px] truncate">{r.product_name || '—'}</td>
        <td className="px-4 py-3 font-heading font-bold text-sm text-brand-accent">{fmtSATxn(r.amount)}</td>
        <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${r.gateway === 'razorpay' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}>{r.gateway}</span></td>
        <td className="px-4 py-3 text-center">{r.gateway === 'cash' || hasPayment ? <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" /> : r.status === 'failed' ? <XCircle className="w-4 h-4 text-red-400 mx-auto" /> : <Clock className="w-4 h-4 text-amber-400 mx-auto" />}</td>
        <td className="px-4 py-3 text-center">{dishtvOk ? <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" /> : dishtvFail ? <XCircle className="w-4 h-4 text-red-500 mx-auto" /> : hasDishtvCall ? <Loader2 className="w-4 h-4 text-indigo-400 mx-auto animate-spin" /> : <span className="text-slate-300 text-xs">—</span>}</td>
        <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border ${s.cls}`}><Icon className="w-3 h-3" />{s.label}</span></td>
        <td className="px-3 py-3 text-center">{isOpen ? <ChevronUp className="w-4 h-4 text-brand-primary" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}</td>
      </tr>
      {isOpen && (
        <tr><td colSpan={11} className="bg-slate-50/80 px-4 py-0">
          <div className="py-5 grid md:grid-cols-2 gap-6 max-w-5xl">
            <div className="space-y-3">
              <h5 className="text-xs font-bold uppercase tracking-widest text-slate-400">Transaction Details</h5>
              <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                <SATxnDetailRow label="Transaction No" value={r.txn_no || '—'} mono />
                <SATxnDetailRow label="Admin" value={r.admin_name || '—'} />
                <SATxnDetailRow label="Customer" value={`${r.customer_name || '—'} (${r.customer_mobile || ''})`} />
                <SATxnDetailRow label="DTH ID" value={r.dth_id || '—'} mono />
                <SATxnDetailRow label="Plan" value={r.product_name || '—'} />
                <SATxnDetailRow label="Charged" value={fmtSATxn(r.amount)} highlight />
                <SATxnDetailRow label="Gateway" value={`${r.gateway}${r.payment_mode ? ` (${r.payment_mode})` : ''}`} />
                {r.gateway_order_id && <SATxnDetailRow label="Order ID" value={r.gateway_order_id} mono />}
                {r.gateway_txn_ref && <SATxnDetailRow label="Payment ID" value={r.gateway_txn_ref} mono />}
                {r.recharge_api_ref && <SATxnDetailRow label="DishTV Ref" value={r.recharge_api_ref} mono />}
                <SATxnDetailRow label="Initiated By" value={`${r.initiated_by_name || ''} (${r.initiated_by_role || ''})`} />
                <SATxnDetailRow label="Created" value={fmtSATxnDate(r.created_at)} />
                {r.failure_reason && <SATxnDetailRow label="Failure Reason" value={r.failure_reason} error />}
              </div>
            </div>
            <div className="space-y-3">
              <h5 className="text-xs font-bold uppercase tracking-widest text-slate-400">Pipeline Activity</h5>
              <div className="bg-white rounded-xl border border-slate-200 p-4"><SATxnPipeline steps={pipeline} /></div>
            </div>
          </div>
        </td></tr>
      )}
    </>
  );
}

function Transactions() {
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [filters, setFilters] = useState({ admin_id: '', status: '', gateway: '' });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      params.page = page; params.limit = PAGE_SIZE;
      const [t, a] = await Promise.all([api.get('/superadmin/transactions', { params }), api.get('/superadmin/admins/list')]);
      setRows(t.data.items || []); setTotal(t.data.total || 0); setAdmins(Array.isArray(a.data) ? a.data : a.data.items || []);
    } catch { toast.error("Failed to load transactions"); }
  };
  useEffect(() => { load(); }, [page]);
  const doSearch = (e) => { e.preventDefault(); if (page === 1) load(); else setPage(1); };

  const totalAmt = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Payment Transactions</h3>
          <p className="text-sm text-slate-500 mt-0.5">All recharge transactions across every admin. {total > 0 && <span className="font-semibold">({total} total)</span>}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => {
            const h = "Date,Admin,Txn No,Customer,DTH ID,Plan,Amount,Gateway,Status\n";
            const b = rows.map(r => [r.created_at?.slice(0, 19), r.admin_name, r.txn_no || '', r.customer_name || '', r.dth_id || '', r.product_name || '', r.amount || 0, r.gateway || '', r.status || ''].join(',')).join('\n');
            const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([h + b], { type: 'text/csv' })); a.download = `sa_txns_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
          }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 px-3 py-2 rounded-lg">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-primary px-3 py-2 rounded-lg">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Page Results</p><p className="font-heading font-black text-2xl text-slate-700 mt-0.5">{rows.length}</p><p className="text-[10px] text-slate-400">of {total} total</p></div>
        <div className="bg-emerald-50 border border-slate-200 rounded-xl px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Success</p><p className="font-heading font-black text-2xl text-emerald-700 mt-0.5">{rows.filter(r => r.status === 'success').length}</p></div>
        <div className="bg-indigo-50 border border-slate-200 rounded-xl px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Volume</p><p className="font-heading font-black text-lg text-brand-primary mt-0.5">{fmtSATxn(totalAmt)}</p><p className="text-[10px] text-slate-400">page total</p></div>
      </div>
      <form onSubmit={doSearch} className="mb-4 bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Admin</label><div className="w-44"><SearchableSelect value={filters.admin_id} onChange={val => setFilters(p => ({ ...p, admin_id: val }))} options={[{ label: "All Admins", value: "" }, ...admins.filter(a => a.status === 'active').map(a => ({ label: a.business_name, value: a.id }))]} /></div></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Status</label><select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-36"><option value="">All</option><option value="success">Success</option><option value="pending">Pending</option><option value="payment_verified">Pmt Verified</option><option value="recharge_failed">Recharge Failed</option><option value="failed">Pmt Failed</option></select></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Gateway</label><select value={filters.gateway} onChange={e => setFilters(p => ({ ...p, gateway: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-32"><option value="">All</option><option value="razorpay">Razorpay</option><option value="cash">Cash</option></select></div>
        <button type="submit" className="bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-semibold px-5 py-2 rounded-md">Filter</button>
      </form>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wider text-slate-400 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Txn No</th><th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Admin</th><th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Plan</th><th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Gateway</th><th className="text-center px-4 py-3">Payment</th>
              <th className="text-center px-4 py-3">DishTV</th><th className="text-left px-4 py-3">Status</th>
              <th className="px-3 py-3 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="px-6 py-10 text-center text-slate-400">No transactions found.</td></tr>}
            {rows.map(r => <SATxnRow key={r.id} r={r} isOpen={openRow === r.id} onToggle={() => setOpenRow(openRow === r.id ? null : r.id)} />)}
          </tbody>
        </table>
        <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
      </div>
    </div>
  );
}

function WebhookEvents() {
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [filters, setFilters] = useState({ admin_id: '', event_type: '' });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      params.page = page;
      params.limit = PAGE_SIZE;
      const [w, a] = await Promise.all([api.get('/superadmin/webhook-events', { params }), api.get('/superadmin/admins/list')]);
      setRows(w.data.items || []);
      setTotal(w.data.total || 0);
      setAdmins(Array.isArray(a.data) ? a.data : a.data.items || []);
    } catch { toast.error("Failed to load webhooks"); }
  };
  useEffect(() => { load(); }, [page]);
  const search = (e) => { e.preventDefault(); if (page === 1) load(); else setPage(1); };

  const [expandedWh, setExpandedWh] = useState(null);
  return (
    <div>
      <div className="mb-6"><h3 className="font-heading font-bold text-2xl text-brand-nav">Webhook Events</h3><p className="text-slate-500 mt-1">Razorpay webhook events received across all admins — useful for debugging.</p></div>
      <form onSubmit={search} className="mb-6 flex flex-wrap gap-3 items-end">
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Admin</label><div className="w-48"><SearchableSelect value={filters.admin_id} onChange={val => setFilters(p => ({ ...p, admin_id: val }))} options={[{ label: "All", value: "" }, ...admins.filter(a => a.status === 'active').map(a => ({ label: a.business_name, value: a.id }))]} /></div></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Event Type</label><select value={filters.event_type} onChange={e => setFilters(p => ({ ...p, event_type: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-48"><option value="">All</option><option value="payment.captured">payment.captured</option><option value="payment.failed">payment.failed</option><option value="order.paid">order.paid</option><option value="refund.created">refund.created</option></select></div>
        <button type="submit" className="bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-semibold px-5 py-2 rounded-md">Filter</button>
      </form>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="text-left px-6 py-3">Time</th><th className="text-left px-6 py-3">Admin</th><th className="text-left px-6 py-3">Event</th><th className="text-left px-6 py-3">Payment ID</th><th className="text-left px-6 py-3">Amount</th><th className="text-left px-6 py-3">Processed</th><th className="px-3 py-3"></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-400">No webhook events yet.</td></tr>}
            {rows.map((r, i) => (
              <React.Fragment key={r.id || i}>
                <tr className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => setExpandedWh(expandedWh === r.id ? null : r.id)}>
                  <td className="px-6 py-3 text-xs text-slate-500 font-mono">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-6 py-3 font-medium text-brand-nav">{r.admin_name}</td>
                  <td className="px-6 py-3"><span className="text-xs font-mono bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{r.event_type}</span></td>
                  <td className="px-6 py-3 text-xs font-mono text-slate-400">{r.razorpay_payment_id || '—'}</td>
                  <td className="px-6 py-3 font-semibold">{r.amount ? `₹${(r.amount / 100).toFixed(2)}` : '—'}</td>
                  <td className="px-6 py-3">{r.processed ? <Badge tone="success">Yes</Badge> : <Badge tone="warn">No</Badge>}</td>
                  <td className="px-3 py-3">{expandedWh === r.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</td>
                </tr>
                {expandedWh === r.id && (
                  <tr className="bg-slate-50/50"><td colSpan={7} className="px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {r.payload_summary && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Payment Details</p>
                          <div className="text-xs bg-white rounded p-3 border border-slate-200 space-y-1">
                            <p><span className="text-slate-400">Order ID:</span> <span className="font-mono">{r.razorpay_order_id || '—'}</span></p>
                            <p><span className="text-slate-400">Method:</span> {r.method || '—'}</p>
                            <p><span className="text-slate-400">Email:</span> {r.payload_summary?.email || '—'}</p>
                            <p><span className="text-slate-400">Contact:</span> {r.payload_summary?.contact || '—'}</p>
                            <p><span className="text-slate-400">Description:</span> {r.payload_summary?.description || '—'}</p>
                            {r.payload_summary?.error_code && <p><span className="text-red-500 font-semibold">Error:</span> {r.payload_summary.error_code} — {r.payload_summary.error_description}</p>}
                          </div>
                        </div>
                      )}
                      {r.full_payload && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Full Webhook Payload</p>
                          <pre className="text-xs bg-white rounded p-3 border border-slate-200 overflow-x-auto font-mono text-slate-600 max-h-60 overflow-y-auto">{JSON.stringify(r.full_payload, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
      </div>
    </div>
  );
}

/* ── SuperAdmin Recharge Logs ──────────────────────────────────────── */
const SA_STATUS_ICONS = {
  success: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  error: <XCircle className="w-4 h-4 text-red-500" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
  info: <Info className="w-4 h-4 text-blue-500" />,
};
const SA_STATUS_BG = {
  success: "bg-green-50 border-green-200",
  error: "bg-red-50 border-red-200",
  warning: "bg-amber-50 border-amber-200",
  info: "bg-blue-50 border-blue-200",
};
const SA_ROLE_BADGE = {
  admin: "bg-indigo-100 text-indigo-700",
  dealer: "bg-purple-100 text-purple-700",
  customer: "bg-sky-100 text-sky-700",
};

function SALogEntry({ log }) {
  const [open, setOpen] = useState(false);
  const ts = log.timestamp?.replace("T", " ").slice(0, 19);
  return (
    <div className={`border rounded-lg mb-2 transition-all ${SA_STATUS_BG[log.status] || "bg-white border-slate-200"}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center gap-3">
        {SA_STATUS_ICONS[log.status] || SA_STATUS_ICONS.info}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-800">{log.event_label}</span>
            {log.admin_name && <span className="text-[10px] font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{log.admin_name}</span>}
            {log.role && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${SA_ROLE_BADGE[log.role] || "bg-slate-100"}`}>{log.role}</span>}
            {log.dth_id && <span className="text-xs font-mono text-slate-400">VC: {log.dth_id}</span>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{log.message}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-slate-400 font-mono">{ts}</p>
          {log.amount != null && <p className="text-xs font-semibold text-slate-600">₹{Number(log.amount).toFixed(2)}</p>}
        </div>
        {(log.request_data || log.response_data) ? (open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />) : <div className="w-4" />}
      </button>
      {open && (log.request_data || log.response_data) && (
        <div className="px-4 pb-3 border-t border-slate-200/50 space-y-2">
          {log.request_data && (
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Request</p>
              <pre className="text-xs bg-white/80 rounded p-2 overflow-x-auto font-mono text-slate-600 max-h-40 overflow-y-auto">{JSON.stringify(log.request_data, null, 2)}</pre>
            </div>
          )}
          {log.response_data && (
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Response</p>
              <pre className="text-xs bg-white/80 rounded p-2 overflow-x-auto font-mono text-slate-600 max-h-40 overflow-y-auto">{JSON.stringify(log.response_data, null, 2)}</pre>
            </div>
          )}
          {log.sim_id && <p className="text-[10px] text-slate-400">SIM: <span className="font-mono">{log.sim_id.slice(0, 8)}</span></p>}
          <p className="text-[10px] text-slate-400">Recharge: <span className="font-mono">{log.recharge_id}</span> | Admin ID: <span className="font-mono">{log.admin_id?.slice(0, 8)}</span></p>
        </div>
      )}
    </div>
  );
}

function SArechargeLogs() {
  const [logs, setLogs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [adminFilter, setAdminFilter] = useState("All");

  const [analyzeTxnId, setAnalyzeTxnId] = useState("");
  const [analyzeInput, setAnalyzeInput] = useState("");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const [searchInput, setSearchInput] = useState("");

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (adminFilter !== "All") params.set('admin_id', adminFilter);
      if (statusFilter !== "All") params.set('status', statusFilter);
      if (search) params.set('search', search);
      params.set('page', page);
      params.set('limit', PAGE_SIZE);

      const [logsRes, adminsRes] = await Promise.all([
        api.get(`/superadmin/recharge-logs?${params.toString()}`),
        api.get("/superadmin/admins/list"),
      ]);
      setLogs(logsRes.data.items || []);
      setTotal(logsRes.data.total || 0);
      setAdmins(Array.isArray(adminsRes.data) ? adminsRes.data : adminsRes.data.items || []);
    } catch { toast.error("Failed to load logs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, adminFilter, statusFilter, search]);

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <FileText className="w-6 h-6 text-brand-primary" /> Recharge Logs (All Admins)
          </h3>
          <p className="text-slate-500 text-sm mt-1">End-to-end pipeline logs across all admins. Filter by admin to diagnose issues.</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:underline disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Transaction Analyzer Search */}
      <div className="bg-gradient-to-r from-brand-primary/5 to-brand-accent/5 border border-brand-primary/20 rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div>
          <h4 className="font-bold text-brand-nav flex items-center gap-2">
            <Activity className="w-5 h-5 text-brand-primary" />
            Transaction Pipeline Analyzer
          </h4>
          <p className="text-xs text-slate-500 mt-1">Deep-dive into a specific transaction's complete lifecycle (Logs, Webhooks, Ledgers).</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (analyzeInput.trim()) setAnalyzeTxnId(analyzeInput.trim()); }} className="flex w-full md:w-auto gap-2">
          <input
            type="text"
            placeholder="Enter Txn No, Order ID..."
            value={analyzeInput}
            onChange={(e) => setAnalyzeInput(e.target.value)}
            className="flex-1 md:w-64 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
          />
          <button type="submit" disabled={!analyzeInput.trim()} className="bg-brand-primary hover:bg-brand-primaryHover text-white px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50 transition">
            Analyze
          </button>
        </form>
      </div>

      {analyzeTxnId && (
        <TransactionAnalyzerModal
          txnId={analyzeTxnId}
          role="superadmin"
          onClose={() => { setAnalyzeTxnId(""); setAnalyzeInput(""); }}
        />
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search logs, DTH, admin..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none" />
        </div>
        <div className="w-48">
          <SearchableSelect value={adminFilter} onChange={setAdminFilter} options={[{ label: "All Admins", value: "All" }, ...admins.filter(a => a.status === 'approved' || a.status === 'active').map(a => ({ label: a.business_name || a.name, value: a.id }))]} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="All">All Status</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="info">Info</option>
        </select>
      </div>
      <p className="text-xs text-slate-400 mb-3">{total} log entries</p>
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-xl">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No recharge logs found.</p>
        </div>
      ) : (
        <>
          <div className="space-y-1">{logs.map((l) => <SALogEntry key={l.id} log={l} />)}</div>
          <div className="mt-3">
            <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
          </div>
        </>
      )}
    </div>
  );
}

/* ── Settlement Report (Route Transfers) ─────────────────────── */
function RouteTransfers() {
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ admin_id: '', status: '' });
  const [todayMetrics, setTodayMetrics] = useState({ pending: 0, settled: 0 });

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      params.page = page;
      params.limit = PAGE_SIZE;
      const [t, a, c] = await Promise.all([
        api.get('/superadmin/transfers', { params }),
        api.get('/superadmin/admins/list'),
        api.get('/superadmin/commission-report', { params: params.admin_id ? { admin_id: params.admin_id } : {} }),
      ]);
      setRows(t.data.items || []);
      setTotal(t.data.total || 0);
      setTodayMetrics({ pending: t.data.today_pending_paise || 0, settled: t.data.today_settled_paise || 0 });
      setAdmins(Array.isArray(a.data) ? a.data : a.data.items || []);
      setCommission(c.data);
    } catch { toast.error("Failed to load transfers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page]);
  const search = (e) => { e.preventDefault(); if (page === 1) load(); else setPage(1); };

  const markSettled = async (id) => {
    if (!window.confirm("Mark this offline settlement as paid?")) return;
    try {
      await api.put(`/superadmin/transfers/${id}/mark-settled`);
      toast.success("Marked as paid");
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const statusTone = (s) => s === 'processed' || s === 'personal_settlement_completed' ? 'success' : s === 'failed' || s === 'error' ? 'danger' : s === 'created' || s === 'personal_settlement_pending' ? 'warn' : 'neutral';
  const fmt = (v) => `₹${(Number(v || 0) / 100).toFixed(2)}`;

  return (
    <div>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <Landmark className="w-6 h-6 text-brand-primary" /> Settlement Report
          </h3>
          <p className="text-slate-500 mt-1">All fund transfers and settlements to admin linked accounts via Razorpay Route.</p>
        </div>
      </div>

      {/* Today's Metrics (SuperAdmin view) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-20"><Clock className="w-16 h-16 text-blue-600" /></div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-1">Expected Today</p>
          <p className="font-heading font-black text-3xl text-blue-900">{fmt(todayMetrics.pending)}</p>
          <p className="text-xs text-blue-600/80 mt-1 font-medium">Processing by Razorpay.</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-20"><CheckCircle2 className="w-16 h-16 text-green-600" /></div>
          <p className="text-xs font-bold uppercase tracking-widest text-green-600 mb-1">Settled Today</p>
          <p className="font-heading font-black text-3xl text-green-900">{fmt(todayMetrics.settled)}</p>
          <p className="text-xs text-green-600/80 mt-1 font-medium">Cleared to LCO connected bank accounts.</p>
        </div>
      </div>

      {/* Commission Summary */}
      {commission && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Total Volume</p>
            <p className="text-xl font-heading font-bold text-slate-800 mt-1">{fmt(commission.grand_total_amount)}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Admin Payouts</p>
            <p className="text-xl font-heading font-bold text-slate-800 mt-1">{fmt(commission.grand_total_admin_payouts)}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">Route Commission</p>
            <p className="text-xl font-heading font-bold text-green-900 mt-1">{fmt(commission.grand_commission_route)}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Offline Pending</p>
            <p className="text-xl font-heading font-bold text-amber-900 mt-1">{fmt(commission.grand_commission_personal_pending)}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Offline Paid</p>
            <p className="text-xl font-heading font-bold text-blue-900 mt-1">{fmt(commission.grand_commission_personal_completed)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <form onSubmit={search} className="mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Admin</label>
          <div className="w-48">
            <SearchableSelect value={filters.admin_id} onChange={val => setFilters(p => ({ ...p, admin_id: val }))} options={[{ label: "All", value: "" }, ...admins.filter(a => a.status === 'active').map(a => ({ label: a.business_name, value: a.id }))]} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
          <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-44">
            <option value="">All</option>
            <option value="processed">Processed (Route)</option>
            <option value="personal_settlement_pending">Offline Pending</option>
            <option value="personal_settlement_completed">Offline Completed</option>
            <option value="created">Created</option>
            <option value="failed">Failed</option>
            <option value="error">Error</option>
          </select>
        </div>
        <button type="submit" className="bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-semibold px-5 py-2 rounded-md">Filter</button>
        <button type="button" onClick={load} disabled={loading} className="flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:underline disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </form>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">Admin</th>
                <th className="text-left px-6 py-3">Linked Account</th>
                <th className="text-right px-6 py-3">Total</th>
                <th className="text-right px-6 py-3">Admin Share</th>
                <th className="text-right px-6 py-3">Commission</th>
                <th className="text-left px-6 py-3">Transfer ID</th>
                <th className="text-left px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="px-6 py-8 text-center text-slate-400">No transfers found.</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-6 py-3 text-xs text-slate-500 font-mono">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-6 py-3 font-medium text-brand-nav">{r.admin_name}</td>
                  <td className="px-6 py-3 text-xs font-mono text-slate-500">{r.linked_account_id || '—'}</td>
                  <td className="px-6 py-3 text-right font-heading font-bold">{fmt(r.total_amount)}</td>
                  <td className="px-6 py-3 text-right font-semibold text-brand-accent">{fmt(r.admin_amount)}</td>
                  <td className="px-6 py-3 text-right font-semibold text-amber-600">{fmt(r.platform_commission)}</td>
                  <td className="px-6 py-3 text-xs font-mono text-slate-400">
                    {r.source === 'personal' ? <Badge tone="info">Offline</Badge> : (r.razorpay_transfer_id || '—').slice(0, 20)}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <Badge tone={statusTone(r.status)}>{r.status.replace('personal_settlement_', 'offline_')}</Badge>
                      {r.status === 'personal_settlement_pending' && (
                        <button onClick={() => markSettled(r.id)} className="text-[10px] bg-brand-primary text-white px-2 py-1 rounded hover:bg-brand-primaryHover">
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
        </div>
      )}

      {/* Per-admin commission breakdown */}
      {commission && commission.admins && commission.admins.length > 0 && (
        <div className="mt-8">
          <h4 className="font-heading font-bold text-lg text-brand-nav mb-4">Commission Breakdown by Admin</h4>
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Admin</th>
                  <th className="text-right px-6 py-3">Transfers</th>
                  <th className="text-right px-6 py-3">Total Volume</th>
                  <th className="text-right px-6 py-3">Admin Payout</th>
                  <th className="text-right px-6 py-3">Platform Commission</th>
                  <th className="text-right px-6 py-3">Route Comm.</th>
                  <th className="text-right px-6 py-3 text-amber-600">Offline Pending</th>
                  <th className="text-right px-6 py-3 text-blue-600">Offline Paid</th>
                </tr>
              </thead>
              <tbody>
                {commission.admins.map((a, i) => (
                  <tr key={a.admin_id || i} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-6 py-3 font-medium text-brand-nav">{a.admin_name}</td>
                    <td className="px-6 py-3 text-right">{a.total_transfers}</td>
                    <td className="px-6 py-3 text-right font-heading font-bold">{fmt(a.total_amount)}</td>
                    <td className="px-6 py-3 text-right font-semibold text-brand-accent">{fmt(a.total_admin_amount)}</td>
                    <td className="px-6 py-3 text-right font-semibold text-slate-700">{fmt(a.total_commission)}</td>
                    <td className="px-6 py-3 text-right font-semibold text-green-600">{fmt(a.commission_route)}</td>
                    <td className="px-6 py-3 text-right font-semibold text-amber-600">{fmt(a.commission_personal_pending)}</td>
                    <td className="px-6 py-3 text-right font-semibold text-blue-600">{fmt(a.commission_personal_completed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SA Complaints (from Admins) ──────────────────────────────── */
const SA_STATUS_COLORS = { open: "bg-blue-50 text-blue-700 border-blue-200", in_progress: "bg-amber-50 text-amber-700 border-amber-200", resolved: "bg-green-50 text-green-700 border-green-200", closed: "bg-slate-100 text-slate-500 border-slate-200" };
const SA_PRI_COLORS = { low: "bg-slate-100 text-slate-600", medium: "bg-amber-50 text-amber-700", high: "bg-red-50 text-red-600" };
const fmtDt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

function SAComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 25 });
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/complaints?${params}`).then(r => {
      setComplaints(r.data.items || []); setTotal(r.data.total || 0);
    }).catch(() => { }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page, statusFilter]);

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try { await api.post(`/complaints/${id}/reply`, { message: replyText }); toast.success("Reply sent"); setReplyText(""); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setReplying(false); }
  };
  const changeStatus = async (id, s) => {
    try { await api.patch(`/complaints/${id}/status`, { status: s }); toast.success(`Status → ${s.replace(/_/g, ' ')}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Admin Complaints</h3>
        <p className="text-slate-500 text-sm mt-1">Issues raised by admins (LCOs) for platform support.</p>
      </div>
      <div className="flex gap-2 flex-wrap mb-4">
        {["all", "open", "in_progress", "resolved", "closed"].map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${statusFilter === s ? "bg-brand-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {s === "all" ? "All" : s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {loading ? <div className="text-slate-400 text-center py-8">Loading…</div> : complaints.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          <Headphones className="w-8 h-8 mx-auto mb-3 text-slate-300" /><p>No admin complaints.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {complaints.map(c => {
            const isOpen = expanded === c.id;
            return (
              <div key={c.id} className={`bg-white rounded-xl border transition-colors ${isOpen ? 'border-brand-primary/30 shadow-sm' : 'border-slate-200'}`}>
                <div className="p-4 cursor-pointer" onClick={() => { setExpanded(isOpen ? null : c.id); setReplyText(""); }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono text-slate-400">{c.ticket_no}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${SA_STATUS_COLORS[c.status] || ''}`}>{c.status?.replace(/_/g, ' ')}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${SA_PRI_COLORS[c.priority] || ''}`}>{c.priority}</span>
                        <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{c.submitted_by_name}</span>
                      </div>
                      <p className="font-semibold text-brand-nav text-sm mt-1">{c.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{c.category} · {fmtDt(c.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.replies?.length > 0 && <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-semibold">{c.replies.length}</span>}
                      {isOpen ? <ChevronUp className="w-4 h-4 text-brand-primary" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    <div className="mt-3 bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">From {c.submitted_by_name} ({c.submitted_by_role}):</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.message}</p>
                    </div>
                    {c.replies?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Replies</p>
                        {c.replies.map(r => (
                          <div key={r.id} className={`rounded-lg p-3 text-sm ${r.by_role === 'superadmin' ? 'bg-indigo-50 border border-indigo-100' : 'bg-slate-50'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-slate-600">{r.by_name} <span className="text-slate-400 font-normal">({r.by_role})</span></span>
                              <span className="text-[10px] text-slate-400">{fmtDt(r.created_at)}</span>
                            </div>
                            <p className="text-slate-700 whitespace-pre-wrap">{r.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {c.status !== 'closed' && (
                      <>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-slate-500">Status:</span>
                          {["in_progress", "resolved", "closed"].filter(s => s !== c.status).map(s => (
                            <button key={s} onClick={() => changeStatus(c.id, s)} className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 capitalize">{s.replace(/_/g, ' ')}</button>
                          ))}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <input placeholder="Reply…" value={expanded === c.id ? replyText : ""} onChange={e => setReplyText(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && sendReply(c.id)}
                            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
                          <button onClick={() => sendReply(c.id)} disabled={replying || !replyText.trim()}
                            className="bg-brand-primary hover:bg-brand-primaryHover text-white px-3 py-2 rounded-lg disabled:opacity-50">
                            {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {total > 25 && (
        <div className="flex justify-center gap-2 pt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-slate-200 text-xs disabled:opacity-40">Prev</button>
          <span className="text-xs text-slate-500 py-1">Page {page} of {Math.ceil(total / 25)}</span>
          <button disabled={page >= Math.ceil(total / 25)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-slate-200 text-xs disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

export default function SuperAdminPage() {
  return (
    <DashLayout navItems={nav} title="SuperAdmin" basePath="/superadmin">
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="admins" element={<Admins />} />
        <Route path="gateways" element={<Gateways />} />
        <Route path="gateway-configs" element={<AdminGatewayConfigs />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="route-transfers" element={<RouteTransfers />} />
        <Route path="recharge-logs" element={<SArechargeLogs />} />
        <Route path="webhooks" element={<WebhookEvents />} />
        <Route path="notifications" element={<NotificationProviders />} />
        <Route path="complaints" element={<SAComplaints />} />
        <Route path="leads" element={<DemoLeads />} />
        <Route path="saas-plans" element={<SaaSPlans />} />
        <Route path="settings" element={<PlatformSettings />} />
      </Routes>
    </DashLayout>
  );
}

function PlatformSettings() {
  const [settings, setSettings] = useState({ support_phone: "", support_email: "", support_whatsapp: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/superadmin/platform-settings").then(r => {
      setSettings({
        support_phone: r.data?.support_phone || "",
        support_email: r.data?.support_email || "",
        support_whatsapp: r.data?.support_whatsapp || "",
      });
    }).catch(() => toast.error("Failed to load settings"));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/superadmin/platform-settings", settings);
      toast.success("Platform settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setSettings(p => ({ ...p, [k]: e.target.value }));

  return (
    <div>
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Platform Settings</h3>
        <p className="text-slate-500 mt-1">Configure global platform details.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-2xl">
        <form onSubmit={save} className="space-y-4">
          <h4 className="font-heading font-semibold text-lg border-b pb-2 mb-4">Support & Helpline</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Support Email</label>
              <input type="email" value={settings.support_email} onChange={set("support_email")} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20" placeholder="support@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Support Phone</label>
              <input type="text" value={settings.support_phone} onChange={set("support_phone")} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20" placeholder="+1234567890" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Support WhatsApp</label>
              <input type="text" value={settings.support_whatsapp} onChange={set("support_whatsapp")} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20" placeholder="+1234567890" />
            </div>
          </div>
          <div className="pt-4">
            <button type="submit" disabled={loading} className="bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-6 py-2.5 rounded-lg shadow-sm transition-colors flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DemoLeads() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append("status_filter", statusFilter);
    api.get(`/billing/admin/leads?${params.toString()}`).then(r => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'new' ? 'contacted' : 'new';
    try {
      await api.put(`/billing/admin/leads/${id}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      load();
    } catch { toast.error("Failed to update status"); }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/billing/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch { toast.error("Failed to delete lead"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Demo Requests</h3>
          <p className="text-slate-500 mt-1">Leads from the public landing page contact form.</p>
        </div>
        <div className="flex items-center gap-4">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20 bg-white">
            <option value="">All Leads</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
          </select>
          <Badge tone="info">{data.length} total leads</Badge>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-medium">
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Business</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Message</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">No leads found.</td></tr>
              ) : data.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 font-medium text-brand-nav">{d.name}</td>
                  <td className="px-6 py-4 text-slate-600">{d.business_name}</td>
                  <td className="px-6 py-4 text-slate-600">
                    <div>{d.mobile}</div>
                    <div className="text-xs text-slate-400">{d.email}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 max-w-xs truncate" title={d.message}>{d.message || "-"}</td>
                  <td className="px-6 py-4 text-center">
                    <Badge tone={d.status === 'new' ? 'primary' : 'success'}>{d.status || 'new'}</Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => toggleStatus(d.id, d.status)} title="Toggle Status" className="p-1.5 rounded-md hover:bg-slate-200 text-slate-600 transition-colors">
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeLead(d.id)} title="Delete Lead" className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
